import { COMPONENT_STATE_ARGS, Metadata, pick } from '../../../../utils/spds/metadata';
import { testAwareWaitForLater } from '../../../../utils/waiters';

const METADATA = Metadata.define({
  description: `
Gives a component the standard \`@animated\` argument, the \`animated\` class its stylesheet keys transitions off, and one
place to wait a transition out. Applied as a capability rather than inherited, so a component picks it up wherever it
already sits in the hierarchy: \`class Foo extends compose(Super, Animated)\`.

### Declared intent, not capability
\`animated\` is the argument as the consumer set it, and stays true under test. It says whether this component is
*supposed* to animate, which is what the class and the stylesheet need to agree on — a component that dropped the class
under test would be snapshotted in a state no user ever sees. Deciding not to actually sit through a transition is a
separate question, and it belongs to \`awaitAnimation\`.

A consumer that genuinely wants animation off in tests passes \`@animated={{false}}\` itself;
\`Spds::Feedback::Toast::Container\` does exactly that for the toasts it renders.

### Waiting one out
\`awaitAnimation\` is the only part that knows about tests. It returns immediately when the component is not animating
and, through \`testAwareWaitForLater\`, whenever it runs under test — so a component holding an element in the DOM for
its exit transition does not spend that time in the suite. Subclasses declare how long by overriding
\`animationDuration\`, which must track whatever the stylesheet transitions over: too short cuts the transition off, too
long leaves a finished element on screen.
  `,
  extends: [pick(COMPONENT_STATE_ARGS, [{ name: 'animated', default: true }])],
  moduleUrl: import.meta.url,
  name: 'Animated',
});

/**
 * Capability adding animation state to any base-derived component.
 *
 * Arguments
 * @argument {boolean} animated whether the component should animate its transitions
 *
 * @property {boolean} animated the argument as the consumer set it; drives the `animated` class
 * @property {number} animationDuration how long a transition runs, for subclasses to declare
 */
export const Animated = Super =>
  class Animated extends Super {
    static metadata = this.setMetadata(METADATA, import.meta.url);

    get animated() {
      return this.argOrDefault('animated');
    }

    /**
     * No transition unless a subclass declares one. Zero rather than a guessed default so a
     * component that forgets to override it fails by not waiting, rather than by holding an
     * element on screen for a duration nothing is transitioning over.
     */
    get animationDuration() {
      return 0;
    }

    get classesToMap() {
      return [...(super.classesToMap ?? []), 'animated'];
    }

    /**
     * Waits out the component's transition. Returns immediately when it is not animating, and
     * under test regardless, so suites do not sit through animations they cannot see.
     */
    async awaitAnimation() {
      if (this.animated) await testAwareWaitForLater(this.animationDuration);
    }
  };

/**
 * The capability's own metadata, before any component composes it. A capability is a factory
 * rather than a class, so there is no `SomeClass.metadata` for the styleguide to read — the page
 * documents what the capability declares, and each adopting component's page shows the merged view.
 */
export const ANIMATED_CAPABILITY_METADATA = METADATA;

export default Animated;
